import { FC, useState, useMemo } from 'react'
import { Box, CircularProgress, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import theme from 'theme'
import { SidebarIcon } from '../icons'
import { BetaTag } from '../../nexaAI.styles'
import { SnowflakeThread } from '../../utils/types'

import {
  SidebarContainer,
  SidebarInnerContent,
  SidebarHeader,
  SidebarTitle,
  SearchContainer,
  SearchInputWrapper,
  SearchInput,
  ThreadsContainer,
  SectionHeader,
  SectionTitle,
  ThreadItem,
  ThreadTitle,
  SessionHistoryContainer,
  NewChatButtonContainer,
  NewChatButton,
  NewChatButtonText,
  ToggleIconButton,
  ThreadMenuButton
} from './nexaSidebar.styles'

const RenameIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6667 1.44775C12.9143 1.44775 13.1595 1.49653 13.3883 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.6667L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const DeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 4H3.33333H14" stroke="#E74C3C" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M5.33334 4.00004V2.66671C5.33334 2.31309 5.47382 1.97395 5.72387 1.7239C5.97392 1.47385 6.31305 1.33337 6.66668 1.33337H9.33334C9.68697 1.33337 10.0261 1.47385 10.2761 1.7239C10.5262 1.97395 10.6667 2.31309 10.6667 2.66671V4.00004M12.6667 4.00004V13.3334C12.6667 13.687 12.5262 14.0261 12.2761 14.2762C12.0261 14.5262 11.687 14.6667 11.3333 14.6667H4.66668C4.31305 14.6667 3.97392 14.5262 3.72387 14.2762C3.47382 14.0261 3.33334 13.687 3.33334 13.3334V4.00004H12.6667Z"
      stroke="#E74C3C"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

interface NexaSidebarProps {
  threads: SnowflakeThread[]
  activeThreadId: string | null
  isLoadingThreads: boolean
  onNewChat: () => void
  onSelectThread: (threadId: string) => void
  onOpenDeleteModal: (threadId: string, threadName: string) => void
  onOpenRenameModal: (threadId: string, threadName: string) => void
  onToggleSidebar?: () => void
  isCollapsed?: boolean
}

const NexaSidebar: FC<NexaSidebarProps> = ({
  threads,
  activeThreadId,
  isLoadingThreads,
  onNewChat,
  onSelectThread,
  onOpenDeleteModal,
  onOpenRenameModal,
  onToggleSidebar,
  isCollapsed = false
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [menuThreadId, setMenuThreadId] = useState<string | null>(null)
  const [menuThreadName, setMenuThreadName] = useState<string>('')

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, threadId: string, threadName: string) => {
    event.stopPropagation()
    setMenuAnchorEl(event.currentTarget)
    setMenuThreadId(threadId)
    setMenuThreadName(threadName)
  }

  const handleMenuClose = () => {
    setMenuAnchorEl(null)
    setMenuThreadId(null)
    setMenuThreadName('')
  }

  const handleRenameClick = () => {
    if (menuThreadId && menuThreadName) {
      onOpenRenameModal(menuThreadId, menuThreadName)
    }
    handleMenuClose()
  }

  const handleDeleteClick = () => {
    if (menuThreadId && menuThreadName) {
      onOpenDeleteModal(menuThreadId, menuThreadName)
    }
    handleMenuClose()
  }

  // Filter threads based on search query
  const filteredThreads = useMemo(() => {
    if (!searchQuery.trim()) return threads
    const query = searchQuery.toLowerCase()
    return threads.filter((thread) => {
      const name = thread.name || 'Untitled'
      return name.toLowerCase().includes(query)
    })
  }, [threads, searchQuery])

  // Get display name for a thread
  const getThreadDisplayName = (thread: SnowflakeThread): string => {
    if (thread.name) return thread.name
    return 'Untitled'
  }

  return (
    <SidebarContainer isCollapsed={isCollapsed}>
      <SidebarInnerContent isCollapsed={isCollapsed}>
        {/* Header */}
        <SidebarHeader>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SidebarTitle>Nexa AI Agent</SidebarTitle>
            <BetaTag>Beta</BetaTag>
          </Box>
          <ToggleIconButton onClick={onToggleSidebar}>
            <SidebarIcon />
          </ToggleIconButton>
        </SidebarHeader>

        {/* Content area with scroll */}
        <ThreadsContainer>
          {/* Search Input */}
          <SearchContainer>
            <SearchInputWrapper>
              <SearchIcon sx={{ color: theme.colors.legacyNeutral100, fontSize: 20 }} />
              <SearchInput
                placeholder="Search chats"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchInputWrapper>
          </SearchContainer>

          {/* Loading State */}
          {isLoadingThreads && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
              <CircularProgress size={24} />
            </div>
          )}

          {/* Session History */}
          {!isLoadingThreads && filteredThreads.length > 0 && (
            <SessionHistoryContainer>
              <SectionHeader>
                <SectionTitle>Session history</SectionTitle>
              </SectionHeader>

              {filteredThreads.map((thread) => {
                const threadId = String(thread.thread_id)
                const displayName = getThreadDisplayName(thread)

                return (
                  <ThreadItem
                    key={threadId}
                    isActive={threadId === activeThreadId}
                    onClick={() => onSelectThread(threadId)}
                  >
                    <ThreadTitle>{displayName}</ThreadTitle>
                    <ThreadMenuButton
                      className="thread-menu-button"
                      size="small"
                      onClick={(e) => handleMenuOpen(e, threadId, displayName)}
                      isOpen={menuThreadId === threadId}
                    >
                      <MoreVertIcon sx={{ fontSize: 18 }} />
                    </ThreadMenuButton>
                  </ThreadItem>
                )
              })}
            </SessionHistoryContainer>
          )}

          {/* Empty State */}
          {!isLoadingThreads && threads.length === 0 && (
            <div style={{ padding: '16px', textAlign: 'center', color: theme.colors.legacyNeutral100 }}>
              No chat history yet
            </div>
          )}
        </ThreadsContainer>

        {/* New Chat Button - Fixed at bottom */}
        <NewChatButtonContainer>
          <NewChatButton onClick={onNewChat}>
            <AddIcon sx={{ fontSize: 18, color: theme.colors.surfaceContainerBackground }} />
            <NewChatButtonText>New Chat</NewChatButtonText>
          </NewChatButton>
        </NewChatButtonContainer>
      </SidebarInnerContent>

      {/* Thread Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        PaperProps={{
          sx: {
            backgroundColor: theme.colors.menuBg,
            border: `1px solid ${theme.colors.menuItemHover}`,
            borderRadius: '12px',
            boxShadow: theme.colors.tooltipShadow,
            minWidth: '120px',
            marginTop: '12px',
            '& .MuiList-root': {
              padding: 0
            }
          }
        }}
      >
        <MenuItem
          onClick={handleRenameClick}
          sx={{
            padding: '16px 16px 10px 16px',
            borderBottom: `1px solid ${theme.colors.menuItemHover}`,
            '&:hover': {
              backgroundColor: theme.colors.menuItemHover
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: '24px', color: theme.colors.textSecondary }}>
            <RenameIcon />
          </ListItemIcon>
          <ListItemText
            primary="Rename"
            primaryTypographyProps={{
              fontFamily: theme.typography.fontFamily.primary,
              fontSize: '14px',
              fontWeight: 500,
              color: theme.colors.foreground,
              lineHeight: '22px'
            }}
          />
        </MenuItem>
        <MenuItem
          onClick={handleDeleteClick}
          sx={{
            padding: '10px 16px 16px 16px',
            '&:hover': {
              backgroundColor: theme.colors.menuItemHover
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: '24px' }}>
            <DeleteIcon />
          </ListItemIcon>
          <ListItemText
            primary="Delete"
            primaryTypographyProps={{
              fontFamily: theme.typography.fontFamily.primary,
              fontSize: '14px',
              fontWeight: 500,
              color: '#e74c3c',
              lineHeight: '22px'
            }}
          />
        </MenuItem>
      </Menu>
    </SidebarContainer>
  )
}

export default NexaSidebar
